/**
 * Upsell Modal Component
 * Displays bundle upsell suggestions to encourage additional service selection
 */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Gift, AlertTriangle } from "lucide-react";

export default function UpsellModal({
  open,
  onOpenChange,
  selectedServiceCount,
  upsellSuggestions,
  onAddService,
  onSkip,
}) {
  const getTitle = () => {
    return selectedServiceCount >= 3 
      ? "Add More & Save More!" 
      : "Add a Service & Save More!";
  };

  const getDescription = () => {
    if (selectedServiceCount === 1) {
      return (
        <>Add just <strong>one more service</strong> to unlock <strong className="text-emerald-600">5% off</strong> your entire order!</>
      );
    }
    if (selectedServiceCount === 2) {
      return (
        <>Add <strong>one more service</strong> to unlock <strong className="text-emerald-600">10% off</strong> your entire order!</>
      );
    }
    return (
      <>While we're at your property, why not add more services at <strong className="text-emerald-600">10% off</strong>?</>
    );
  };

  const getSubheading = () => {
    return selectedServiceCount >= 3 
      ? "While we're there, consider adding:" 
      : "Popular add-ons:";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="upsell-modal">
        <DialogHeader>
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-center text-xl">
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-center">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            {getSubheading()}
          </p>
          {upsellSuggestions.map((service) => (
            <button
              key={service.service_id}
              onClick={() => onAddService(service)}
              className="w-full p-3 bg-gradient-to-r from-blue-50 to-white border border-blue-200 rounded-lg hover:border-blue-400 transition-all text-left group"
              data-testid={`upsell-service-${service.service_id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-900 group-hover:text-blue-700">{service.service_name}</span>
                <div className="text-right">
                  <span className="text-slate-400 line-through text-sm mr-2">${service.originalPrice}</span>
                  <span className="text-blue-600 font-bold">${service.bundlePrice}</span>
                </div>
              </div>
              <p className="text-xs text-emerald-600 mt-1">
                Save ${service.savings} with bundle pricing!
              </p>
              {service.service_id === 'windows' && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  All exterior window screens must be removed by property owner prior to cleaning
                </p>
              )}
              {service.service_id === 'gutters' && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Open gutters only - does not include gutters with screens
                </p>
              )}
            </button>
          ))}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onSkip}
            className="w-full sm:w-auto"
            data-testid="upsell-skip-btn"
          >
            No thanks, continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
